import React, {useCallback} from 'react';
import {ErrorCode, FileError, useDropzone} from 'react-dropzone';
import {default as BaseDropZone} from '@views/orders/OrderFilesList/DropZone';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import {notNull} from '@src/helpers/ts/notNull';

const ACCEPT: Record<string, string[]> = {
    'text/csv': ['.csv'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

const ALLOWED = {
    MIMES: Object.keys(ACCEPT),
    EXTENSIONS: Object.values(ACCEPT).flat(),
};

const validator = (file: File | File[] | FileList | null): FileError | null => {
    if (!(file instanceof File)) {
        return null;
    }

    const extension = file.name?.toLowerCase().replace(/.*\.(.+)$/, '.$1');
    if (!ALLOWED.MIMES.includes(file.type) || !ALLOWED.EXTENSIONS.includes(extension)) {
        return {
            message: 'List:ImportRecords.errors.file-format-is-not-allowed',
            code: ErrorCode.FileInvalidType,
        };
    }

    return null;
};

export default function DropZone(props: {
    onDrop: (files: File[]) => any,
}) {
    const {onDrop: baseOnDrop} = props;

    const onDrop = useCallback((list: FileList | File[]) => {
        const files: File[] = [].filter.call((list instanceof File) ? [list] : list, notNull);

        baseOnDrop(files);
    }, [baseOnDrop]);

    const dragState = useDropzone({
        onDrop: onDrop,
        preventDropOnDocument: false,
        accept: ACCEPT,
        validator
    });

    const {fileRejections} = dragState;

    return <>
        {fileRejections.map(item => {
            const message = item.errors.at(-1)?.message;
            if (!message) {
                return null;
            }

            return <Translate
                    key={item.file.name}
                    tag="div"
                    message={message}
                    className="alert alert-danger"
                    replace={{
                        'allowed-extensions': ALLOWED.EXTENSIONS.join(', '),
                    }}
            />;
        })}

        <BaseDropZone {...dragState} tabIndex={0}>
            <div className="text-center text-muted">
                <Translate
                        tag="div"
                        message="List:ImportRecords.dropzone.click-or-drop-file-here"
                />
                <Translate
                        tag="div"
                        message="List:ImportRecords.dropzone.allowed-extensions"
                        className="mt-2"
                        replace={{
                            'allowed-extensions': ALLOWED.EXTENSIONS.map((extension, index, list) => {
                                return <span key={extension} className={classnames('badge badge-soft-info', {
                                    'me-1': list.length !== (index + 1),
                                })}>{extension}</span>;
                            }),
                        }}
                        withComponentsAsReplacements
                />
            </div>
        </BaseDropZone>
    </>;
}
